'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import { Button, Card, Heading, Text } from '@zillow/constellation-10';
import { Calendar, Mail } from 'lucide-react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { Module } from '@/src/components/features/property/ui/Module/Module';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { useScheduledToursForUser } from '@/src/lib/hooks/query/useHpToursQuery';
import { useContactFormAction } from '@/src/components/features/property/ContactForm';
import { useOptionalHostedTourAction, useTourAction } from '@/src/components/features/property/TourForm';
import {
  formatTourDateTime,
  getScheduledTour,
  getTourProvider,
  TOUR_TYPE_LABELS,
} from '@/src/components/features/property/TourForm/tourUtils';
import { appendTimeISOSubstringIfMissing, isDateInThePast } from '@/src/components/features/property/lib/dateUtils';
import * as styles from './tourConfirmationBanner.styles';

type Props = {
  listing: PropertyDetailListing;
};

const MAX_TIMER_DELAY_MS = 2147483647;

export const TourConfirmationBanner: React.FC<Props> = ({ listing }) => {
  const { data: currentUser } = useCurrentUser();
  const { data: scheduledTours } = useScheduledToursForUser(currentUser?.loggedIn ?? false);
  const { openContactForm, contactFormOverlay, identityResolved } = useContactFormAction(listing);
  // Shared wizard where the page has one; its own elsewhere. See TourActionHost.
  const hostedTour = useOptionalHostedTourAction('home_details_halo', listing);
  const ownTour = useTourAction(listing);
  const { openTour, openTourReschedule, isTourReady } = hostedTour ?? ownTour;
  const tourOverlay = hostedTour ? null : ownTour.tourOverlay;
  const scheduledTour = getScheduledTour(listing.aliasEncoded, scheduledTours);
  const scheduledDateTime = appendTimeISOSubstringIfMissing(scheduledTour?.startTime ?? '');
  const [isPast, setIsPast] = useState(() => Boolean(scheduledDateTime && isDateInThePast(scheduledDateTime)));

  useEffect(() => {
    if (!scheduledDateTime) return;

    if (isDateInThePast(scheduledDateTime)) {
      setIsPast(true);
      return;
    }

    setIsPast(false);
    const msUntilPast = new Date(scheduledDateTime).getTime() - Date.now();
    const timer = window.setTimeout(() => setIsPast(true), Math.min(msUntilPast, MAX_TIMER_DELAY_MS));
    return () => window.clearTimeout(timer);
  }, [scheduledDateTime]);

  if (!scheduledTour) return null;

  const provider = getTourProvider(listing.details?.instantTourProviders, scheduledTour.tourType);
  const dateOnly = !scheduledTour.startTime.includes('T') || provider?.dateOnly;
  const tourTypeLabel = TOUR_TYPE_LABELS[scheduledTour.tourType] ?? 'Tour';

  return (
    <>
      <Module id="tour-confirmation-module" title="Your booked tour">
        <Card elevated={false}>
          <Card.Body className={styles.bannerBody} role="status">
            <div className={styles.summary}>
              <Heading level={3} textStyle="heading-xs">
                Booked {tourTypeLabel.toLowerCase()}
              </Heading>
              <Text textStyle="body-sm" color="text.subtle">
                {formatTourDateTime(scheduledDateTime, scheduledTour.zoneId, dateOnly)}
              </Text>
            </div>
            <div className={styles.actions}>
              <Button
                type="button"
                tone="brand"
                emphasis="outlined"
                size="sm"
                icon={<LucideIcon icon={Mail} size="sm" />}
                onClick={openContactForm}
                disabled={!identityResolved}
                aria-busy={!identityResolved}
              >
                Contact
              </Button>
              <Button
                type="button"
                tone="brand"
                emphasis="outlined"
                size="sm"
                icon={<LucideIcon icon={Calendar} size="sm" />}
                onClick={isPast ? openTour : openTourReschedule}
                disabled={!isTourReady}
                aria-busy={!isTourReady}
              >
                {isPast ? 'Book another' : 'Reschedule'}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Module>
      {contactFormOverlay}
      {tourOverlay}
    </>
  );
};
